// src/pages/Partner/PartnerLearners.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Activity,
  Building2,
  CheckCircle2,
  Clock3,
  Eye,
  Mail,
  MessageSquareText,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";

import {
  Link,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";

import AddExistingLearnerModal from "../../components/partner/AddExistingLearnerModal";

import {
  fetchPartnerCohorts,
} from "../../lib/partner/partnerCohorts";

import {
  removeLearnerFromCohort,
  updateCohortLearnerStatus,
} from "../../lib/partner/partnerLearners";

import {
  fetchPartnerLearnerRoster,
} from "../../lib/partner/partnerLearnerRoster";

import {
  fetchPartnerLearnerFacilitators,
} from "../../lib/partner/partnerFacilitators";

import {
  fetchMyPartnerSupportRequests,
} from "../../lib/partner/partnerSupport";


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatDate(
  value
) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function formatDateTime(
  value
) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}


function timestamp(
  value
) {
  if (!value) {
    return 0;
  }

  const time =
    new Date(
      value
    ).getTime();

  return Number.isNaN(time)
    ? 0
    : time;
}


function statusClasses(
  status
) {
  switch (status) {
    case "active":
      return [
        "border-green-200",
        "bg-green-50",
        "text-green-700",
      ].join(" ");

    case "completed":
      return [
        "border-blue-200",
        "bg-blue-50",
        "text-blue-700",
      ].join(" ");

    default:
      return [
        "border-amber-200",
        "bg-amber-50",
        "text-amber-800",
      ].join(" ");
  }
}


function supportClasses(
  status
) {
  switch (status) {
    case "open":
      return [
        "border-red-200",
        "bg-red-50",
        "text-red-700",
      ].join(" ");

    case "acknowledged":
      return [
        "border-amber-200",
        "bg-amber-50",
        "text-amber-800",
      ].join(" ");

    default:
      return [
        "border-gray-200",
        "bg-gray-50",
        "text-gray-600",
      ].join(" ");
  }
}


function supportLabel(
  status
) {
  switch (status) {
    case "open":
      return "Open";

    case "acknowledged":
      return "Acknowledged";

    default:
      return status || "Support";
  }
}


function activityLabel(
  learner
) {
  const days =
    learner
      .days_since_activity;

  if (
    days === null ||
    days === undefined
  ) {
    return "No activity";
  }

  const number =
    Number(days);

  if (
    !Number.isFinite(
      number
    )
  ) {
    return "No activity";
  }

  if (number === 0) {
    return "Today";
  }

  if (number === 1) {
    return "Yesterday";
  }

  return `${number} days ago`;
}


function latestActivity(
  learner
) {
  const completedTime =
    timestamp(
      learner
        .last_completed_at
    );

  const activityTime =
    timestamp(
      learner
        .last_activity_at
    );

  /*
   * If the latest completed level is also the latest
   * meaningful programme event, show the actual level.
   */
  if (
    completedTime > 0 &&
    completedTime >=
      activityTime
  ) {
    return {
      title:
        learner
          .last_completed_level_title
          ? `Completed ${learner.last_completed_level_title}`
          : "Completed a learning level",

      date:
        learner
          .last_completed_at,

      type: "Learning",
    };
  }

  /*
   * Activity occurred after the latest known level
   * completion. We do not invent what that activity was.
   */
  if (
    activityTime > 0
  ) {
    return {
      title:
        "Programme activity recorded",

      date:
        learner
          .last_activity_at,

      type: "Activity",
    };
  }

  return {
    title:
      "No programme activity recorded",

    date: null,

    type: "None",
  };
}


/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function PartnerLearners() {
  const {
    access,
  } = useOutletContext();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();


  /* ------------------------------- Filters -------------------------------- */

  const selectedCohortId =
    searchParams.get(
      "cohort"
    ) || "";

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState(
    "current"
  );


  /* -------------------------------- Data ---------------------------------- */

  const [
    cohorts,
    setCohorts,
  ] = useState([]);

  const [
    learners,
    setLearners,
  ] = useState([]);

  const [
    facilitatorAssignments,
    setFacilitatorAssignments,
  ] = useState([]);

  const [
    supportRequests,
    setSupportRequests,
  ] = useState([]);


  /* -------------------------------- State --------------------------------- */

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    addingLearner,
    setAddingLearner,
  ] = useState(false);

  const [
    changingLearnerId,
    setChangingLearnerId,
  ] = useState("");


  /* -------------------------------- Roles --------------------------------- */

  const role =
    access?.partner_role ||
    "";

  const canViewFacilitatorTags =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(role);

  /*
   * Preserve the existing production Support access rule.
   *
   * We do not expose learner support information to roles
   * that cannot already access the Support inbox.
   */
  const canUseSupport =
    [
      "facilitator",
      "cohort_manager",
    ].includes(role);


  /* --------------------------- Selected cohort ---------------------------- */

  const selectedCohort =
    useMemo(
      () =>
        cohorts.find(
          (cohort) =>
            String(
              cohort.id
            ) ===
            String(
              selectedCohortId
            )
        ) || null,
      [
        cohorts,
        selectedCohortId,
      ]
    );


  const canManageSelectedCohort =
    Boolean(
      selectedCohort
        ?.can_manage
    );


  /* ------------------------------- Loading -------------------------------- */

  const loadData =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const [
            cohortRows,
            learnerRows,
            facilitatorRows,
            supportRows,
          ] =
            await Promise.all([
              fetchPartnerCohorts(),

              fetchPartnerLearnerRoster(
                selectedCohortId ||
                  null
              ),

              canViewFacilitatorTags
                ? fetchPartnerLearnerFacilitators(
                    selectedCohortId ||
                      null
                  )
                : Promise.resolve(
                    []
                  ),

              canUseSupport
                ? fetchMyPartnerSupportRequests()
                : Promise.resolve(
                    []
                  ),
            ]);

          setCohorts(
            Array.isArray(
              cohortRows
            )
              ? cohortRows
              : []
          );

          setLearners(
            Array.isArray(
              learnerRows
            )
              ? learnerRows
              : []
          );

          setFacilitatorAssignments(
            Array.isArray(
              facilitatorRows
            )
              ? facilitatorRows
              : []
          );

          setSupportRequests(
            Array.isArray(
              supportRows
            )
              ? supportRows
              : []
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner learner roster loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The learner records could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        selectedCohortId,
        canViewFacilitatorTags,
        canUseSupport,
      ]
    );


  useEffect(() => {
    loadData();
  }, [loadData]);


  /* -------------------------- Facilitator lookup -------------------------- */

  const facilitatorByLearnerId =
    useMemo(() => {
      return new Map(
        facilitatorAssignments.map(
          (
            assignment
          ) => [
            String(
              assignment
                .cohort_learner_id
            ),

            assignment,
          ]
        )
      );
    }, [
      facilitatorAssignments,
    ]);


  /* ---------------------------- Support lookup ---------------------------- */

  const supportByLearnerId =
    useMemo(() => {
      const map =
        new Map();

      for (
        const request of supportRequests
      ) {
        if (
          ![
            "open",
            "acknowledged",
          ].includes(
            request.status
          )
        ) {
          continue;
        }

        const key =
          String(
            request
              .cohort_learner_id ||
              ""
          );

        if (!key) {
          continue;
        }

        const current =
          map.get(key);

        /*
         * Prefer an open request over acknowledged,
         * otherwise keep the newest active request.
         */
        if (!current) {
          map.set(
            key,
            request
          );

          continue;
        }

        if (
          request.status ===
            "open" &&
          current.status !==
            "open"
        ) {
          map.set(
            key,
            request
          );

          continue;
        }

        if (
          request.status ===
            current.status &&
          timestamp(
            request.created_at
          ) >
            timestamp(
              current.created_at
            )
        ) {
          map.set(
            key,
            request
          );
        }
      }

      return map;
    }, [
      supportRequests,
    ]);


  /* ------------------------------- Summary -------------------------------- */

  const summary =
    useMemo(() => {
      return learners.reduce(
        (
          totals,
          learner
        ) => {
          totals.total += 1;

          if (
            learner.status ===
            "active"
          ) {
            totals.active +=
              1;
          }

          if (
            learner.status ===
            "completed"
          ) {
            totals.completed +=
              1;
          }

          return totals;
        },
        {
          total: 0,
          active: 0,
          completed: 0,
        }
      );
    }, [learners]);


  const supportAttentionCount =
    useMemo(() => {
      return Array.from(
        supportByLearnerId
          .values()
      ).length;
    }, [
      supportByLearnerId,
    ]);


  /* ------------------------------- Search --------------------------------- */

  const filteredLearners =
    useMemo(() => {
      const cleanSearch =
        search
          .trim()
          .toLowerCase();

      return learners.filter(
        (learner) => {
          const matchesStatus =
            statusFilter ===
              "all" ||
            (
              statusFilter ===
                "current" &&
              [
                "active",
                "completed",
              ].includes(
                learner.status
              )
            ) ||
            learner.status ===
              statusFilter;

          if (!matchesStatus) {
            return false;
          }

          if (!cleanSearch) {
            return true;
          }

          const facilitator =
            facilitatorByLearnerId.get(
              String(
                learner
                  .cohort_learner_id
              )
            );

          return [
            learner.full_name,
            learner.email,
            learner.business_name,
            learner.industry,
            learner.province,
            learner.cohort_name,
            facilitator
              ?.facilitator_full_name,
            facilitator
              ?.facilitator_email,
          ].some(
            (value) =>
              String(
                value || ""
              )
                .toLowerCase()
                .includes(
                  cleanSearch
                )
          );
        }
      );
    }, [
      learners,
      search,
      statusFilter,
      facilitatorByLearnerId,
    ]);


  /* ---------------------------- Cohort filter ----------------------------- */

  function handleCohortChange(
    event
  ) {
    const nextCohortId =
      event.target.value;

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    if (nextCohortId) {
      nextParams.set(
        "cohort",
        nextCohortId
      );
    } else {
      nextParams.delete(
        "cohort"
      );
    }

    setSearchParams(
      nextParams
    );
  }


  /* --------------------------- Membership status -------------------------- */

  async function handleStatusChange(
    learner,
    nextStatus
  ) {
    if (
      nextStatus ===
      learner.status
    ) {
      return;
    }

    try {
      setChangingLearnerId(
        learner
          .cohort_learner_id
      );

      setError("");

      await updateCohortLearnerStatus(
        {
          cohortLearnerId:
            learner
              .cohort_learner_id,

          status:
            nextStatus,
        }
      );

      await loadData();
    } catch (
      statusError
    ) {
      console.error(
        "Learner status update failed:",
        statusError
      );

      setError(
        statusError?.message ||
          "The learner status could not be updated."
      );
    } finally {
      setChangingLearnerId(
        ""
      );
    }
  }


  /* ------------------------------- Remove --------------------------------- */

  async function handleRemove(
    learner
  ) {
    const confirmed =
      window.confirm(
        `Remove ${
          learner.full_name ||
          learner.email
        } from ${
          learner.cohort_name
        }? Their existing record will be retained as removed.`
      );

    if (!confirmed) {
      return;
    }

    try {
      setChangingLearnerId(
        learner
          .cohort_learner_id
      );

      setError("");

      await removeLearnerFromCohort(
        learner
          .cohort_learner_id
      );

      /*
       * The roster RPC intentionally does not return
       * removed memberships, so this learner disappears
       * from the operational roster after refresh.
       */
      await loadData();
    } catch (
      removeError
    ) {
      console.error(
        "Learner removal failed:",
        removeError
      );

      setError(
        removeError?.message ||
          "The learner could not be removed."
      );
    } finally {
      setChangingLearnerId(
        ""
      );
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      {/* HEADER */}

      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Partner · Learners
          </p>

          <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
            Learners
          </h1>

          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
            View learner membership, business context, recent programme activity and support information for{" "}
            {access.partner_name}.
          </p>
        </div>


        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={
              loadData
            }
            disabled={
              loading
            }
            className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] bg-white px-4 py-3 text-sm font-black text-[#555555] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-50"
          >
            <RefreshCw
              className={[
                "mr-2 h-4 w-4",
                loading
                  ? "animate-spin"
                  : "",
              ].join(" ")}
            />

            Refresh
          </button>


          {selectedCohort &&
            canManageSelectedCohort &&
            selectedCohort.status !==
              "archived" && (
              <button
                type="button"
                onClick={() =>
                  setAddingLearner(
                    true
                  )
                }
                className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
              >
                <UserPlus className="mr-2 h-4 w-4" />

                Add existing learner
              </button>
            )}
        </div>
      </header>


      {/* SUMMARY */}

      <section
        className={[
          "mt-7 grid gap-4 sm:grid-cols-2",
          canUseSupport
            ? "xl:grid-cols-4"
            : "xl:grid-cols-3",
        ].join(" ")}
      >
        <SummaryCard
          label="Learners"
          value={
            summary.total
          }
          description={
            selectedCohort
              ? "Visible in this cohort"
              : "Across accessible cohorts"
          }
          icon={
            Users
          }
        />

        <SummaryCard
          label="Active"
          value={
            summary.active
          }
          description="Currently participating"
          icon={
            Activity
          }
        />

        <SummaryCard
          label="Completed"
          value={
            summary.completed
          }
          description="Programme membership completed"
          icon={
            CheckCircle2
          }
        />

        {canUseSupport && (
          <SummaryCard
            label="Needs attention"
            value={
              supportAttentionCount
            }
            description="Open learner support"
            icon={
              MessageSquareText
            }
          />
        )}
      </section>


      {/* FILTERS */}

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(220px,320px)_minmax(260px,1fr)_190px]">

          {/* Cohort */}

          <div>
            <label
              htmlFor="learner-cohort-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Cohort
            </label>

            <select
              id="learner-cohort-filter"
              value={
                selectedCohortId
              }
              onChange={
                handleCohortChange
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-bold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="">
                All accessible cohorts
              </option>

              {cohorts.map(
                (
                  cohort
                ) => (
                  <option
                    key={
                      cohort.id
                    }
                    value={
                      cohort.id
                    }
                  >
                    {
                      cohort.name
                    }
                  </option>
                )
              )}
            </select>
          </div>


          {/* Search */}

          <div>
            <label
              htmlFor="learner-search"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Search
            </label>

            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="learner-search"
                value={
                  search
                }
                onChange={(
                  event
                ) =>
                  setSearch(
                    event
                      .target
                      .value
                  )
                }
                placeholder={
                  canViewFacilitatorTags
                    ? "Search learner, business, cohort or facilitator"
                    : "Search learner, business or cohort"
                }
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>


          {/* Status */}

          <div>
            <label
              htmlFor="learner-status-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Status
            </label>

            <select
              id="learner-status-filter"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event
                    .target
                    .value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-bold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="current">
                Current learners
              </option>

              <option value="active">
                Active
              </option>

              <option value="completed">
                Completed
              </option>

              <option value="all">
                All visible
              </option>
            </select>
          </div>
        </div>
      </section>


      {/* ERROR */}

      {error && (
        <div
          role="alert"
          className="mt-5 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 sm:flex-row sm:items-center sm:justify-between"
        >
          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              loadData
            }
            className="inline-flex flex-none items-center justify-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>
        </div>
      )}


      {/* LOADING */}

      {loading ? (
        <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center shadow-sm">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

          <p className="mt-3 text-sm font-semibold text-[#757575]">
            Loading learners…
          </p>
        </div>
      ) : filteredLearners.length ===
        0 ? (
        <EmptyState
          selectedCohort={
            selectedCohort
          }
          canManageSelectedCohort={
            canManageSelectedCohort
          }
          onAdd={() =>
            setAddingLearner(
              true
            )
          }
        />
      ) : (
        <>
          {/* ============================================================ */}
          {/* DESKTOP TABLE                                                */}
          {/* ============================================================ */}

          <section className="mt-6 hidden overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm lg:block">
            <div className="overflow-x-auto">
              <table className="min-w-[1220px] divide-y divide-[#EFEFEF]">
                <thead className="bg-[#FFF9F5]">
                  <tr>
                    <TableHeading>
                      Learner / business
                    </TableHeading>

                    <TableHeading>
                      Cohort
                    </TableHeading>

                    {canViewFacilitatorTags && (
                      <TableHeading>
                        Facilitator
                      </TableHeading>
                    )}

                    <TableHeading>
                      Status
                    </TableHeading>

                    <TableHeading>
                      Last active
                    </TableHeading>

                    <TableHeading>
                      Latest activity
                    </TableHeading>

                    {canUseSupport && (
                      <TableHeading>
                        Support
                      </TableHeading>
                    )}

                    <TableHeading align="right">
                      Actions
                    </TableHeading>
                  </tr>
                </thead>


                <tbody className="divide-y divide-[#EFEFEF]">
                  {filteredLearners.map(
                    (
                      learner
                    ) => {
                      const learnerId =
                        String(
                          learner
                            .cohort_learner_id
                        );

                      const facilitator =
                        facilitatorByLearnerId.get(
                          learnerId
                        );

                      const support =
                        supportByLearnerId.get(
                          learnerId
                        );

                      const latest =
                        latestActivity(
                          learner
                        );

                      const isChanging =
                        changingLearnerId ===
                        learner
                          .cohort_learner_id;

                      return (
                        <tr
                          key={
                            learner
                              .cohort_learner_id
                          }
                          className="align-top transition hover:bg-[#FFFDFB]"
                        >
                          {/* Learner / Business */}

                          <td className="px-5 py-4">
                            <div className="min-w-[220px]">
                              <p className="font-black text-[#333333]">
                                {learner.full_name ||
                                  "Learner"}
                              </p>

                              <a
                                href={`mailto:${learner.email}`}
                                className="mt-1 inline-flex items-center text-xs font-semibold text-[#757575] transition hover:text-[#FF914D]"
                              >
                                <Mail className="mr-1.5 h-3.5 w-3.5" />

                                {
                                  learner.email
                                }
                              </a>

                              <div className="mt-3 rounded-xl bg-[#FFF9F5] px-3 py-2">
                                <p className="text-sm font-black text-[#444444]">
                                  {learner.business_name ||
                                    "Business not added"}
                                </p>

                                {(learner.industry ||
                                  learner.province) && (
                                  <p className="mt-0.5 text-xs font-semibold text-[#757575]">
                                    {[
                                      learner.industry,
                                      learner.province,
                                    ]
                                      .filter(
                                        Boolean
                                      )
                                      .join(
                                        " · "
                                      )}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>


                          {/* Cohort */}

                          <td className="px-5 py-4">
                            <Link
                              to={`/partner/cohorts/${learner.cohort_id}`}
                              className="inline-flex min-w-[140px] items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
                            >
                              <Building2 className="h-4 w-4 flex-none" />

                              {
                                learner.cohort_name
                              }
                            </Link>
                          </td>


                          {/* Facilitator */}

                          {canViewFacilitatorTags && (
                            <td className="px-5 py-4">
                              {facilitator
                                ?.facilitator_partner_member_id ? (
                                <div className="min-w-[150px]">
                                  <p className="text-sm font-black text-[#333333]">
                                    {facilitator.facilitator_full_name ||
                                      "Facilitator"}
                                  </p>

                                  {facilitator.facilitator_email && (
                                    <p className="mt-1 text-xs font-semibold text-[#757575]">
                                      {
                                        facilitator.facilitator_email
                                      }
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-black text-gray-600">
                                  Unassigned
                                </span>
                              )}
                            </td>
                          )}


                          {/* Status */}

                          <td className="px-5 py-4">
                            {learner.can_manage ? (
                              <select
                                value={
                                  learner.status
                                }
                                onChange={(
                                  event
                                ) =>
                                  handleStatusChange(
                                    learner,
                                    event
                                      .target
                                      .value
                                  )
                                }
                                disabled={
                                  isChanging
                                }
                                className="rounded-full border border-[#FFE1CF] bg-white px-3 py-2 text-xs font-black capitalize outline-none transition focus:border-[#FF914D] disabled:opacity-50"
                              >
                                <option value="active">
                                  Active
                                </option>

                                <option value="completed">
                                  Completed
                                </option>
                              </select>
                            ) : (
                              <span
                                className={[
                                  "inline-flex rounded-full border px-3 py-1 text-xs font-black capitalize",
                                  statusClasses(
                                    learner.status
                                  ),
                                ].join(
                                  " "
                                )}
                              >
                                {
                                  learner.status
                                }
                              </span>
                            )}

                            <p className="mt-2 text-[11px] font-semibold text-[#999999]">
                              Joined{" "}
                              {formatDate(
                                learner
                                  .joined_at
                              )}
                            </p>
                          </td>


                          {/* Last active */}

                          <td className="px-5 py-4">
                            <div className="min-w-[120px]">
                              <div className="flex items-center gap-2">
                                <Clock3 className="h-4 w-4 flex-none text-[#FF914D]" />

                                <p className="text-sm font-black text-[#333333]">
                                  {activityLabel(
                                    learner
                                  )}
                                </p>
                              </div>

                              {learner.last_activity_at && (
                                <p className="mt-1 text-xs font-semibold text-[#757575]">
                                  {formatDateTime(
                                    learner
                                      .last_activity_at
                                  )}
                                </p>
                              )}
                            </div>
                          </td>


                          {/* Latest activity */}

                          <td className="px-5 py-4">
                            <div className="min-w-[210px] max-w-[280px]">
                              <p className="text-sm font-black leading-relaxed text-[#333333]">
                                {
                                  latest.title
                                }
                              </p>

                              {latest.date && (
                                <p className="mt-1 text-xs font-semibold text-[#757575]">
                                  {formatDateTime(
                                    latest.date
                                  )}
                                </p>
                              )}
                            </div>
                          </td>


                          {/* Support */}

                          {canUseSupport && (
                            <td className="px-5 py-4">
                              {support ? (
                                <Link
                                  to={`/partner/support?request=${encodeURIComponent(
                                    support.request_id
                                  )}`}
                                  className={[
                                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black transition hover:opacity-80",
                                    supportClasses(
                                      support.status
                                    ),
                                  ].join(
                                    " "
                                  )}
                                >
                                  <MessageSquareText className="h-3.5 w-3.5" />

                                  {supportLabel(
                                    support.status
                                  )}
                                </Link>
                              ) : (
                                <span className="text-sm font-semibold text-[#AAAAAA]">
                                  —
                                </span>
                              )}
                            </td>
                          )}


                          {/* Actions */}

                          <td className="px-5 py-4 text-right">
                            <LearnerActions
                              learner={
                                learner
                              }
                              isChanging={
                                isChanging
                              }
                              onRemove={
                                handleRemove
                              }
                            />
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </section>


          {/* ============================================================ */}
          {/* MOBILE / TABLET CARDS                                        */}
          {/* ============================================================ */}

          <section className="mt-6 grid gap-4 lg:hidden">
            {filteredLearners.map(
              (
                learner
              ) => {
                const learnerId =
                  String(
                    learner
                      .cohort_learner_id
                  );

                const facilitator =
                  facilitatorByLearnerId.get(
                    learnerId
                  );

                const support =
                  supportByLearnerId.get(
                    learnerId
                  );

                const latest =
                  latestActivity(
                    learner
                  );

                const isChanging =
                  changingLearnerId ===
                  learner
                    .cohort_learner_id;

                return (
                  <article
                    key={
                      learner
                        .cohort_learner_id
                    }
                    className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm"
                  >
                    {/* Identity */}

                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-lg font-black text-[#333333]">
                          {learner.full_name ||
                            "Learner"}
                        </p>

                        <a
                          href={`mailto:${learner.email}`}
                          className="mt-1 inline-flex max-w-full items-center text-xs font-semibold text-[#757575]"
                        >
                          <Mail className="mr-1.5 h-3.5 w-3.5 flex-none" />

                          <span className="truncate">
                            {
                              learner.email
                            }
                          </span>
                        </a>
                      </div>

                      <span
                        className={[
                          "inline-flex flex-none rounded-full border px-3 py-1 text-xs font-black capitalize",
                          statusClasses(
                            learner.status
                          ),
                        ].join(
                          " "
                        )}
                      >
                        {
                          learner.status
                        }
                      </span>
                    </div>


                    {/* Business */}

                    <div className="mt-4 rounded-2xl border border-[#FFEDE1] bg-[#FFF9F5] p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-[#FF914D]">
                        Business
                      </p>

                      <p className="mt-1 font-black text-[#333333]">
                        {learner.business_name ||
                          "Business not added"}
                      </p>

                      {(learner.industry ||
                        learner.province) && (
                        <p className="mt-1 text-xs font-semibold text-[#757575]">
                          {[
                            learner.industry,
                            learner.province,
                          ]
                            .filter(
                              Boolean
                            )
                            .join(
                              " · "
                            )}
                        </p>
                      )}
                    </div>


                    {/* Details */}

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">

                      <MobileDetail
                        label="Cohort"
                        value={
                          learner
                            .cohort_name
                        }
                      />

                      {canViewFacilitatorTags && (
                        <MobileDetail
                          label="Facilitator"
                          value={
                            facilitator
                              ?.facilitator_full_name ||
                            "Unassigned"
                          }
                        />
                      )}

                      <MobileDetail
                        label="Last active"
                        value={
                          activityLabel(
                            learner
                          )
                        }
                        secondary={
                          learner
                            .last_activity_at
                            ? formatDateTime(
                                learner
                                  .last_activity_at
                              )
                            : ""
                        }
                      />

                      <MobileDetail
                        label="Joined"
                        value={
                          formatDate(
                            learner
                              .joined_at
                          )
                        }
                      />
                    </div>


                    {/* Latest activity */}

                    <div className="mt-4 rounded-2xl border border-[#EFEFEF] p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-[#757575]">
                        Latest activity
                      </p>

                      <p className="mt-2 text-sm font-black leading-relaxed text-[#333333]">
                        {
                          latest.title
                        }
                      </p>

                      {latest.date && (
                        <p className="mt-1 text-xs font-semibold text-[#757575]">
                          {formatDateTime(
                            latest.date
                          )}
                        </p>
                      )}
                    </div>


                    {/* Support */}

                    {canUseSupport &&
                      support && (
                        <Link
                          to={`/partner/support?request=${encodeURIComponent(
                            support.request_id
                          )}`}
                          className={[
                            "mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-black",
                            supportClasses(
                              support.status
                            ),
                          ].join(
                            " "
                          )}
                        >
                          <MessageSquareText className="h-4 w-4" />

                          Support:{" "}
                          {supportLabel(
                            support.status
                          )}
                        </Link>
                      )}


                    {/* Management status */}

                    {learner.can_manage && (
                      <div className="mt-4">
                        <label className="block text-[10px] font-black uppercase tracking-wider text-[#757575]">
                          Membership status
                        </label>

                        <select
                          value={
                            learner.status
                          }
                          onChange={(
                            event
                          ) =>
                            handleStatusChange(
                              learner,
                              event
                                .target
                                .value
                            )
                          }
                          disabled={
                            isChanging
                          }
                          className="mt-2 w-full rounded-2xl border border-[#FFE1CF] bg-white px-4 py-3 text-sm font-black capitalize outline-none focus:border-[#FF914D] disabled:opacity-50"
                        >
                          <option value="active">
                            Active
                          </option>

                          <option value="completed">
                            Completed
                          </option>
                        </select>
                      </div>
                    )}


                    <div className="mt-5 border-t border-[#EFEFEF] pt-4">
                      <LearnerActions
                        learner={
                          learner
                        }
                        isChanging={
                          isChanging
                        }
                        onRemove={
                          handleRemove
                        }
                        mobile
                      />
                    </div>
                  </article>
                );
              }
            )}
          </section>
        </>
      )}


      {/* ADD EXISTING LEARNER */}

      <AddExistingLearnerModal
        open={
          addingLearner
        }
        cohortId={
          selectedCohort?.id
        }
        cohortName={
          selectedCohort?.name
        }
        onClose={() =>
          setAddingLearner(
            false
          )
        }
        onAdded={
          loadData
        }
      />
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Actions                                                                    */
/* -------------------------------------------------------------------------- */

function LearnerActions({
  learner,
  isChanging,
  onRemove,
  mobile = false,
}) {
  return (
    <div
      className={[
        "flex gap-2",
        mobile
          ? "flex-col sm:flex-row"
          : "items-center justify-end",
      ].join(" ")}
    >
      <Link
        to={`/partner/learners/${learner.cohort_learner_id}`}
        className={[
          "inline-flex items-center justify-center rounded-full border border-[#FF914D] px-3 py-2 text-xs font-black text-[#FF914D] transition hover:bg-[#FFF4EC]",

          mobile
            ? "flex-1"
            : "",
        ].join(" ")}
      >
        <Eye className="mr-1.5 h-3.5 w-3.5" />

        View details
      </Link>


      {learner.can_manage && (
        <button
          type="button"
          onClick={() =>
            onRemove(
              learner
            )
          }
          disabled={
            isChanging
          }
          className={[
            "inline-flex items-center justify-center rounded-full border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 transition hover:bg-red-100 disabled:opacity-50",

            mobile
              ? "flex-1"
              : "",
          ].join(" ")}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />

          {isChanging
            ? "Updating…"
            : "Remove"}
        </button>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Empty state                                                                */
/* -------------------------------------------------------------------------- */

function EmptyState({
  selectedCohort,
  canManageSelectedCohort,
  onAdd,
}) {
  return (
    <section className="mt-6 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
        <Users className="h-7 w-7" />
      </div>

      <h2 className="mt-5 text-xl font-black text-[#333333]">
        No learners found
      </h2>

      <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
        {selectedCohort
          ? `No matching learner records are available in ${selectedCohort.name}.`
          : "No matching learner records are available across the cohorts you may access."}
      </p>

      {selectedCohort &&
        canManageSelectedCohort &&
        selectedCohort.status !==
          "archived" && (
          <button
            type="button"
            onClick={
              onAdd
            }
            className="mt-6 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <UserPlus className="mr-2 h-4 w-4" />

            Add existing learner
          </button>
        )}
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* Summary card                                                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black text-[#333333]">
            {value}
          </p>

          <p className="mt-1 text-sm text-[#757575]">
            {description}
          </p>
        </div>

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Mobile detail                                                              */
/* -------------------------------------------------------------------------- */

function MobileDetail({
  label,
  value,
  secondary = "",
}) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-wider text-[#999999]">
        {label}
      </p>

      <p className="mt-1 text-sm font-black text-[#333333]">
        {value ||
          "Not available"}
      </p>

      {secondary && (
        <p className="mt-0.5 text-xs font-semibold text-[#757575]">
          {secondary}
        </p>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Table heading                                                              */
/* -------------------------------------------------------------------------- */

function TableHeading({
  children,
  align = "left",
}) {
  return (
    <th
      scope="col"
      className={[
        "whitespace-nowrap px-5 py-3 text-xs font-black uppercase tracking-wider text-[#757575]",

        align === "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}